import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealSubmissionModal from "../../components/DealSubmissionModal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Briefcase,
  Activity,
  CheckCircle2,
  Plus,
  FileText,
} from "lucide-react";

const WholesalerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDealModal, setShowDealModal] = useState(false);

  // Helper to get auth headers from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, dealsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true, headers }),
        axios.get(`${API}/deals`, { withCredentials: true, headers }),
      ]);
      setStats(statsRes.data);
      setDeals(dealsRes.data.deals || dealsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDealSuccess = () => {
    fetchData();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      single_family: "Single Family",
      multi_family: "Multi-Family",
      apartment: "Apartment",
      commercial: "Commercial",
      land: "Land",
      mixed_use: "Mixed Use",
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-orange-500/10 text-orange-500",
      active: "bg-green-500/10 text-green-500",
      approved: "bg-blue-500/10 text-blue-500",
      closed: "bg-muted text-muted-foreground",
    };
    return styles[status] || "bg-muted text-muted-foreground";
  };

  return (
    <DashboardLayout title="Wholesaler Dashboard">
      <div
        className="space-y-6 animate-fade-in"
        data-testid="wholesaler-dashboard"
      >
        {/* Welcome section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              Welcome back, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your deals and connect with investors
            </p>
          </div>
          <Button
            className="bg-purple-500 hover:bg-purple-600"
            onClick={() => setShowDealModal(true)}
            data-testid="submit-new-deal-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Submit New Deal
          </Button>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="stat-card" data-testid="stat-my-deals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                My Deals
              </CardTitle>
              <div className="stat-card-icon bg-purple-500/10">
                <Briefcase className="w-5 h-5 text-purple-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">
                {loading ? "-" : deals.length}
              </div>
              <p className="stat-card-label">Total submitted</p>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-active-deals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Deals
              </CardTitle>
              <div className="stat-card-icon bg-orange-500/10">
                <Activity className="w-5 h-5 text-orange-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">
                {loading
                  ? "-"
                  : deals.filter(
                      (d) => d.status === "active" || d.status === "approved",
                    ).length}
              </div>
              <p className="stat-card-label">Currently live</p>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-closed-deals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Closed Deals
              </CardTitle>
              <div className="stat-card-icon bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">
                {loading
                  ? "-"
                  : deals.filter((d) => d.status === "closed").length}
              </div>
              <p className="stat-card-label">Successfully closed</p>
            </CardContent>
          </Card>
        </div>

        {/* Total potential profit */}
        {deals.length > 0 && (
          <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Potential Profit
                  </p>
                  <p className="text-3xl font-bold font-heading text-foreground">
                    {formatCurrency(
                      deals.reduce((acc, deal) => {
                        const profit =
                          deal.estimated_arv -
                          deal.asking_price -
                          deal.estimated_rehab_cost;
                        return acc + (profit > 0 ? profit : 0);
                      }, 0),
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">
                    Avg. Wholesale Fee
                  </p>
                  <p className="text-xl font-semibold text-purple-500">
                    {formatCurrency(
                      deals.filter((d) => d.wholesale_fee).length > 0
                        ? deals
                            .filter((d) => d.wholesale_fee)
                            .reduce((acc, d) => acc + d.wholesale_fee, 0) /
                            deals.filter((d) => d.wholesale_fee).length
                        : 0,
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent deals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">Your Deals</CardTitle>
            {deals.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {deals.length} total
              </span>
            )}
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon mx-auto" />
                <p className="empty-state-title">No deals yet</p>
                <p className="empty-state-desc">
                  Start by submitting your first wholesale deal
                </p>
                <Button
                  className="mt-4 bg-purple-500 hover:bg-purple-600"
                  onClick={() => setShowDealModal(true)}
                  data-testid="empty-submit-deal-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Submit New Deal
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {deals.slice(0, 5).map((deal) => (
                  <div
                    key={deal.deal_id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid={`deal-item-${deal.deal_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-foreground">
                          {deal.property_address}
                        </h4>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(deal.status)}`}
                        >
                          {deal.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {deal.city}, {deal.state} {deal.zip_code} •{" "}
                        {getPropertyTypeLabel(deal.property_type)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground">
                        {formatCurrency(deal.asking_price)}
                      </p>
                      <p className="text-sm text-green-500">
                        Profit:{" "}
                        {formatCurrency(
                          deal.estimated_arv -
                            deal.asking_price -
                            deal.estimated_rehab_cost,
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <DealSubmissionModal
        open={showDealModal}
        onOpenChange={setShowDealModal}
        onSuccess={handleDealSuccess}
      />
    </DashboardLayout>
  );
};

export default WholesalerDashboard;
